import * as Collapsible from '@radix-ui/react-collapsible'
import { format } from 'date-fns'
import { da, de, enGB, fr, it, nl } from 'date-fns/locale'
import { useEffect, useRef, useState } from 'react'

import SvgIcon from '@/components/svg-icon/svgIcon'
import { useClickOutside } from '@/hooks/useClickOutside'
import { useDevice } from '@/hooks/useDevice'
import { ContentContainer } from '@/layout/contentContainer'
import { cn } from '@/libs/tw'
import { useAppConfigStore } from '@/stores/app-store-config/appConfigStore'
import { useCartStore } from '@/stores/cart-store/cartStore'
import { translate } from '@/utils/translator'

import { ProgressStep } from './progressStep'
import { TotalBookingPrice } from './totelBookingPrice'

export const BookingProgress = () => {
	const { isTablet } = useDevice()
	const [isScrolled, setIsScrolled] = useState(false)
	const [headerHeight, setHeaderHeight] = useState('64px')
	const [open, setOpen] = useState(false)
	const rootUri = useAppConfigStore.use.rootUri()

	const navbarRef = useRef<HTMLElement>(null)
	const ref = useClickOutside<HTMLDivElement>(() => setOpen(false))

	useEffect(() => {
		const handleScroll = () => {
			const position = window.scrollY
			setIsScrolled(position > 0)
		}

		window.addEventListener('scroll', handleScroll)

		return () => {
			window.removeEventListener('scroll', handleScroll)
		}
	}, [])

	useEffect(() => {
		if (!navbarRef.current) return

		const target = navbarRef.current

		const resizeObserver = new ResizeObserver((entries) => {
			const navbar = entries[0]

			setHeaderHeight(`${(navbar.target as HTMLElement).offsetHeight}px`)
		})

		resizeObserver.observe(target)

		return () => resizeObserver.unobserve(target)
	}, [])

	return (
		<>
			<div
				style={{
					height: headerHeight,
				}}
				className={cn('bg-progressbar')}
			></div>
			<header
				ref={navbarRef}
				className={cn(
					'w-screen bg-progressbar py-8 text-progressbar-text xl:flex xl:justify-center',
					'transition duration-300 ease-in-out',
					'fixed left-0 top-0 z-50',
					isScrolled && 'drop-shadow-md'
				)}
			>
				<div ref={ref}>
					<ContentContainer>
						{isTablet ? (
							<Desktop rootUri={rootUri} />
						) : (
							<Mobile open={open} setOpen={setOpen} rootUri={rootUri} />
						)}
					</ContentContainer>
				</div>
			</header>
		</>
	)
}

type DesktopProps = {
	rootUri: string
}
const Desktop = ({ rootUri }: DesktopProps) => (
	<div className={cn('flex w-full justify-center gap-12 lg:justify-normal')}>
		<a
			href={rootUri}
			className={cn('h-logo w-logo shrink-0 bg-logo-image bg-center bg-no-repeat')}
		></a>
		<nav className={cn('flex items-start')}>
			<Location />
			<RoomConfig />
			<TravelPeriod />
			<RoomSelection />
			<ProgressStep
				step="GUEST_DATA"
				number="05"
				title={translate('step-05-progess-title')}
				summary=""
			/>
			<ProgressStep
				step="PAYMENT"
				number="06"
				title={translate('step-06-progess-title')}
				summary=""
			/>
			<TotalBookingPrice />
		</nav>
	</div>
)

type MobileProps = {
	open: boolean
	setOpen: (value: boolean) => void
	rootUri: string
}
const Mobile = ({ open, setOpen, rootUri }: MobileProps) => {
	return (
		<Collapsible.Root
			className={cn('flex w-full flex-col justify-center gap-16 lg:justify-normal')}
			open={open}
			onOpenChange={setOpen}
		>
			<div className={cn('flex items-center justify-between p-2')}>
				<a
					href={rootUri}
					className={cn(
						'h-logoMobile w-logoMobile shrink-0 bg-logo-image bg-center bg-no-repeat'
					)}
				></a>
				<div className={cn('flex')}>
					<TotalBookingPrice />
					<Collapsible.Trigger asChild className={cn('group')}>
						<button>
							<SvgIcon
								collectionName="ibe"
								iconName="chevron"
								className="h-24 w-24 transition-transform duration-300 ease-[cubic-bezier(0.87,_0,_0.13,_1)] group-data-[state=closed]:rotate-180"
							/>
						</button>
					</Collapsible.Trigger>
				</div>
			</div>
			<Collapsible.Content className="overflow-hidden data-[state=closed]:animate-slideUpC data-[state=open]:animate-slideDownC">
				<nav className={cn('flex flex-col')}>
					<Location onClick={() => setOpen(false)} />
					<RoomConfig onClick={() => setOpen(false)} />
					<TravelPeriod onClick={() => setOpen(false)} />
					<RoomSelection onClick={() => setOpen(false)} />
					<ProgressStep
						onClick={() => setOpen(false)}
						step="GUEST_DATA"
						number="05"
						title={translate('step-05-progess-title')}
						summary=""
					/>
					<ProgressStep
						onClick={() => setOpen(false)}
						step="PAYMENT"
						number="06"
						title={translate('step-06-progess-title')}
						summary=""
					/>
				</nav>
			</Collapsible.Content>
		</Collapsible.Root>
	)
}

const Location = ({ onClick }: { onClick?: () => void }) => {
	const location = useCartStore.use.location()

	return (
		<ProgressStep
			className={cn('lg:shrink-0')}
			step="LOCATION"
			number="01"
			title={translate('step-01-progess-title')}
			summary={location?.title}
			onClick={onClick}
		/>
	)
}

const RoomConfig = ({ onClick }: { onClick?: () => void }) => {
	const roomConfigurations = useCartStore.use.roomConfigurations()

	return (
		<ProgressStep
			step="ROOM_CONFIG"
			number="02"
			title={translate('step-02-progess-title')}
			summary={`${roomConfigurations.length !== 0 && roomConfigurations.length} ${roomConfigurations.length !== 0 && (roomConfigurations.length === 1 ? translate('room-config-room') : translate('room-config-rooms'))}`}
			onClick={onClick}
		/>
	)
}

const getLocaleForLangkey = (langKey: string) => {
	switch (langKey) {
		case 'de':
			return de
		case 'da':
			return da
		case 'en':
			return enGB
		case 'it':
			return it
		case 'nl':
			return nl
		case 'fr':
			return fr
		default:
			return de
	}
}

const TravelPeriod = ({ onClick }: { onClick?: () => void }) => {
	const { startDate, endDate } = useCartStore.use.travelPeriod()
	const language = useAppConfigStore.use.language()

	// @TODO: dynamic locales
	const formattedStartDate = startDate
		? format(startDate, 'iiiiii d MMM', { locale: getLocaleForLangkey(language) })
		: ''
	const formattedEndDate = endDate
		? format(endDate, 'iiiiii d MMM', { locale: getLocaleForLangkey(language) })
		: ''

	return (
		<ProgressStep
			step="TRAVEL_PERIOD"
			number="03"
			title={translate('step-03-progess-title')}
			onClick={onClick}
			summary={
				<>
					<span>{formattedStartDate}</span>
					<span>{` \u2192 `}</span>
					<span className={cn('block xl:inline')}>{formattedEndDate}</span>
				</>
			}
		/>
	)
}

const RoomSelection = ({ onClick }: { onClick?: () => void }) => {
	const { selectedRooms } = useCartStore.use.roomSelection()

	return (
		<ProgressStep
			className={cn('grow-0')}
			step="ROOM_SELECTION"
			number="04"
			title={translate('step-04-progess-title')}
			onClick={onClick}
			summary={
				<div className={cn('flex flex-col')}>
					{selectedRooms.map(({ room }, index) => (
						<div
							key={'progress' + room.roomTypeCode + index}
							className={cn('line-clamp-1')}
						>
							{room.title}
						</div>
					))}
				</div>
			}
		/>
	)
}
